import { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  Handle,
  Position,
  useReactFlow,
  type NodeProps,
  type Node,
  type Viewport,
} from '@xyflow/react';
import dagre from '@dagrejs/dagre';
import * as ContextMenu from '@radix-ui/react-context-menu';
import { ChevronDown, ChevronRight, Plus, UserRound, AlertCircle, Network } from 'lucide-react';
import type { OrganizationSnapshot, OrganizationPosition } from './model';
import { isProposedRole } from './model';
import {
  reportingGraph,
  selectedRoleIds,
  visibleRoleIds,
  type EntitySelection,
  type StructureChange,
} from './graph';
import '@xyflow/react/dist/style.css';

interface RoleData extends Record<string, unknown> {
  role: OrganizationPosition;
  division?: string;
  team?: string;
  childCount: number;
  collapsed: boolean;
  highlighted: boolean;
  context: boolean;
  issue?: string;
  status?: StructureChange['status'];
  canEdit: boolean;
  onSelect: (selection: EntitySelection) => void;
  onCollapse: (id: string) => void;
  onQuickEdit?: (roleId: string, mode: 'people' | 'reporting') => void;
}
type RoleFlowNode = Node<RoleData, 'role'>;

const RoleCard = memo(function RoleCard({ data, selected }: NodeProps<RoleFlowNode>) {
  const { role } = data;
  const pendingQuickEdit = useRef<'people' | 'reporting' | null>(null);
  const quickEditFrame = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (quickEditFrame.current !== null) cancelAnimationFrame(quickEditFrame.current);
    },
    []
  );
  return (
    <ContextMenu.Root modal={false}>
      <ContextMenu.Trigger asChild>
        <div
          data-role-id={role.id}
          className={`org-role-card ${selected ? 'is-selected' : ''} ${data.highlighted ? 'is-highlighted' : ''} ${data.context ? 'is-context' : ''} ${data.status ? `org-change-${data.status}` : ''}`}
        >
          <Handle type="target" position={Position.Top} isConnectable={false} />
          <button
            type="button"
            className="org-role-main nopan nodrag"
            onClick={(event) => {
              event.stopPropagation();
              data.onSelect({ kind: 'position', id: role.id });
            }}
            aria-label={`View ${role.title}`}
          >
            <div className="org-role-heading">
              <span className="org-role-mark">
                <Network size={14} />
              </span>
              <strong title={role.title}>{role.title}</strong>
              {data.issue && (
                <AlertCircle size={14} className="org-role-issue" aria-label={data.issue} />
              )}
            </div>
            <div className="org-role-groups">
              {isProposedRole(role) && <span>Proposed role</span>}
              {data.division && <span title={data.division}>{data.division}</span>}
              {data.team && <span title={data.team}>{data.team}</span>}
              {!data.division && !data.team && (
                <span className="org-role-ungrouped">No group assigned</span>
              )}
            </div>
            <div className="org-role-occupants">
              {role.assignments.length ? (
                <>
                  <span className="org-avatar">
                    {role.assignments[0].userPhoto ? (
                      <img src={role.assignments[0].userPhoto} alt="" />
                    ) : (
                      initials(role.assignments[0].userName)
                    )}
                  </span>
                  <span title={role.assignments.map((a) => a.userName).join(', ')}>
                    {role.assignments[0].userName}
                    {role.assignments.length > 1 && <small> +{role.assignments.length - 1}</small>}
                  </span>
                </>
              ) : (
                <>
                  <span className="org-avatar org-avatar-vacant">
                    <UserRound size={14} />
                  </span>
                  <span className="org-muted">
                    {isProposedRole(role) ? 'Unassigned' : 'Vacant role'}
                  </span>
                </>
              )}
            </div>
          </button>
          <div className="org-role-footer">
            <button
              type="button"
              className="org-branch-toggle nopan nodrag"
              onClick={(event) => {
                event.stopPropagation();
                data.onCollapse(role.id);
              }}
              disabled={!data.childCount}
              aria-label={`${data.collapsed ? 'Expand' : 'Collapse'} reports for ${role.title}`}
              aria-expanded={data.childCount ? !data.collapsed : undefined}
            >
              {data.childCount ? (
                data.collapsed ? (
                  <ChevronRight size={13} />
                ) : (
                  <ChevronDown size={13} />
                )
              ) : null}
              <span>
                {data.childCount} direct {data.childCount === 1 ? 'report' : 'reports'}
              </span>
            </button>
            {data.canEdit && (
              <button
                type="button"
                className="org-node-add nopan nodrag"
                aria-label={`Add direct report to ${role.title}`}
                onClick={(event) => {
                  event.stopPropagation();
                  data.onSelect({
                    kind: 'position',
                    parentId: role.id,
                    divisionId: role.divisionId,
                  });
                }}
              >
                <Plus size={14} />
              </button>
            )}
          </div>
          <Handle type="source" position={Position.Bottom} isConnectable={false} />
        </div>
      </ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content
          className="org-role-context-menu"
          onCloseAutoFocus={(event) => {
            const mode = pendingQuickEdit.current;
            pendingQuickEdit.current = null;
            if (!mode) return;
            event.preventDefault();
            // Let the menu release its focus scope before opening a modal editor.
            quickEditFrame.current = requestAnimationFrame(() => {
              quickEditFrame.current = null;
              data.onQuickEdit?.(role.id, mode);
            });
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <ContextMenu.Label className="org-context-title">{role.title}</ContextMenu.Label>
          <ContextMenu.Item onSelect={() => data.onSelect({ kind: 'position', id: role.id })}>
            <Network size={15} />
            {data.canEdit ? 'Edit role' : 'View role details'}
          </ContextMenu.Item>
          {data.canEdit && (
            <>
              <ContextMenu.Item
                onSelect={() => {
                  pendingQuickEdit.current = 'people';
                }}
              >
                <UserRound size={15} />
                Change people
              </ContextMenu.Item>
              <ContextMenu.Item
                onSelect={() => {
                  pendingQuickEdit.current = 'reporting';
                }}
              >
                <Network size={15} />
                Change reporting line
              </ContextMenu.Item>
              <ContextMenu.Item
                onSelect={() =>
                  data.onSelect({
                    kind: 'position',
                    parentId: role.id,
                    divisionId: role.divisionId,
                  })
                }
              >
                <Plus size={15} />
                Add direct report
              </ContextMenu.Item>
            </>
          )}
          {data.childCount > 0 && (
            <>
              <ContextMenu.Separator className="org-context-separator" />
              <ContextMenu.Item onSelect={() => data.onCollapse(role.id)}>
                {data.collapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                {data.collapsed ? 'Expand reports' : 'Collapse reports'}
              </ContextMenu.Item>
            </>
          )}
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
});
const nodeTypes = { role: RoleCard };
export const initials = (name: string) =>
  name
    ?.split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase() || '?';

export interface ReportingCanvasProps {
  snapshot: OrganizationSnapshot;
  selection: EntitySelection | null;
  onSelect: (selection: EntitySelection) => void;
  onQuickEdit?: (roleId: string, mode: 'people' | 'reporting') => void;
  canEdit?: boolean;
  search?: string;
  group?: string;
  changes?: StructureChange[];
  label?: string;
  viewport?: Viewport;
  onViewportChange?: (viewport: Viewport) => void;
}

export default function ReportingCanvas(props: ReportingCanvasProps) {
  return (
    <ReactFlowProvider>
      <Canvas {...props} />
    </ReactFlowProvider>
  );
}

function Canvas({
  snapshot,
  selection,
  onSelect,
  onQuickEdit,
  canEdit = false,
  search = '',
  group = '',
  changes = [],
  label,
  viewport,
  onViewportChange,
}: ReportingCanvasProps) {
  const [collapsed, setCollapsed] = useState(new Set<string>());
  const flow = useReactFlow<RoleFlowNode>();
  const initialFit = useRef(false);
  const canvasRoot = useRef<HTMLDivElement>(null);
  const toggle = (id: string) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const { nodes, edges, issuesCount } = useMemo(() => {
    const graph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
    graph.setGraph({ rankdir: 'TB', nodesep: 32, ranksep: 70, marginx: 35, marginy: 35 });
    const { parents, issues } = reportingGraph(snapshot);
    const { visible, matches } = visibleRoleIds(snapshot, search, group, collapsed);
    const highlighted = selectedRoleIds(snapshot, selection);
    const roles = snapshot.positions
      .filter((p) => visible.has(p.id))
      .sort((a, b) => a.title.localeCompare(b.title) || a.id.localeCompare(b.id));
    roles.forEach((p) => graph.setNode(p.id, { width: 240, height: 170 }));
    roles.forEach((p) => {
      const parent = parents.get(p.id);
      if (parent && visible.has(parent)) graph.setEdge(parent, p.id);
    });
    dagre.layout(graph);
    const nodes: RoleFlowNode[] = roles.map((role) => {
      const point = graph.node(role.id);
      return {
        id: role.id,
        type: 'role',
        position: { x: point.x - 120, y: point.y - 85 },
        width: 240,
        height: 170,
        selected: selection?.kind === 'position' && selection.id === role.id,
        data: {
          role,
          division: role.divisionId
            ? snapshot.divisions.find((d) => d.id === role.divisionId)?.name ||
              'Unavailable division'
            : undefined,
          team: role.teamId
            ? snapshot.teams.find((t) => t.id === role.teamId)?.name || 'Unavailable team'
            : undefined,
          childCount: snapshot.positions.filter((p) => parents.get(p.id) === role.id).length,
          collapsed: collapsed.has(role.id),
          highlighted: highlighted.has(role.id),
          context: Boolean((search || group) && !matches.has(role.id)),
          issue: issues.get(role.id),
          status: changes.find((c) => c.kind === 'position' && c.id === role.id)?.status,
          canEdit,
          onSelect,
          onQuickEdit,
          onCollapse: toggle,
        },
      };
    });
    const edges = roles.flatMap((role) => {
      const parent = parents.get(role.id);
      return parent && visible.has(parent)
        ? [
            {
              id: `${parent}:${role.id}`,
              source: parent,
              target: role.id,
              type: 'smoothstep',
              style: {
                stroke: changes.some(
                  (c) =>
                    c.kind === 'position' &&
                    c.id === role.id &&
                    c.details.includes('Reporting line changed')
                )
                  ? '#b78720'
                  : '#bbc8dc',
                strokeWidth: 1.5,
              },
            },
          ]
        : [];
    });
    return { nodes, edges, issuesCount: issues.size };
  }, [snapshot, selection, search, group, collapsed, canEdit, changes, onSelect, onQuickEdit]);

  useEffect(() => {
    if (!nodes.length) return;
    const id = requestAnimationFrame(() => {
      if (!initialFit.current && viewport) flow.setViewport(viewport);
      else flow.fitView({ padding: 0.2, maxZoom: 1, duration: initialFit.current ? 180 : 0 });
      initialFit.current = true;
    });
    return () => cancelAnimationFrame(id);
  }, [snapshot.company.id, search, group, collapsed, nodes.length]);
  useEffect(() => {
    if (viewport && initialFit.current) {
      const current = flow.getViewport();
      if (
        Math.abs(current.x - viewport.x) > 0.1 ||
        Math.abs(current.y - viewport.y) > 0.1 ||
        Math.abs(current.zoom - viewport.zoom) > 0.001
      )
        void flow.setViewport(viewport);
    }
  }, [viewport?.x, viewport?.y, viewport?.zoom]);

  useEffect(() => {
    if (!canvasRoot.current) return;
    let dimensions: string | undefined;
    let frame = 0;
    const observer = new ResizeObserver(([entry]) => {
      const next = `${entry.contentRect.width}:${entry.contentRect.height}`;
      if (dimensions !== undefined && next !== dimensions && initialFit.current) {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          void flow.fitView({ padding: 0.2, maxZoom: 1 });
        });
      }
      dimensions = next;
    });
    observer.observe(canvasRoot.current);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [flow]);

  return (
    <div className="org-flow" ref={canvasRoot} aria-label={label || 'Organization reporting chart'}>
      <ReactFlow<RoleFlowNode>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        edgesFocusable={false}
        deleteKeyCode={null}
        minZoom={0.005}
        maxZoom={3}
        panOnDrag
        panOnScroll={false}
        zoomOnScroll
        zoomOnPinch
        proOptions={{ hideAttribution: true }}
        selectionOnDrag={false}
        onMoveEnd={(_, next) => onViewportChange?.(next)}
        onNodeClick={(_, node) => onSelect({ kind: 'position', id: node.id })}
        ariaLabelConfig={{
          'node.a11yDescription.default':
            'Tab to the View role button and press Enter. Drag the background to pan.',
          'controls.fitView.ariaLabel': 'Fit organization chart to screen',
        }}
      >
        <Background color="#dbe3ef" gap={22} size={1} />
        <Controls showInteractive={false} position="bottom-right" />
      </ReactFlow>
      {label && <span className="org-canvas-label">{label}</span>}
      {issuesCount > 0 && (
        <span className="org-canvas-notice">
          <AlertCircle size={13} />
          {issuesCount} {issuesCount === 1 ? 'role needs' : 'roles need'} a structure review
        </span>
      )}
      {!nodes.length && (
        <div className="org-canvas-empty">
          <Network size={26} />
          <h3>{snapshot.positions.length ? 'No matching roles' : 'Your structure starts here'}</h3>
          <p>
            {snapshot.positions.length
              ? 'Try a different search or clear the group filter.'
              : 'Add your first role or build a suggested structure with the guided setup.'}
          </p>
        </div>
      )}
      <span className="org-canvas-hint">Drag to pan · Scroll or pinch to zoom</span>
    </div>
  );
}
